// Where a device's spare parts actually live.
//
// Sign-off clears `spareParts` off the extinguisher/hose record so the next
// maintenance opens clean and a copy carries nothing (client #86, #87). The
// parts fitted are written to the inspection instead, on
// `Inspection.readings[].spareParts`.
//
// The rapport and the invoice were still reading the device field, so every
// report line and every parts position went blank the moment a visit was
// signed off — the reports the client saw (client #94), and silently the
// invoice too, which stopped billing fitted parts altogether.
//
// Anything that wants to show or charge for parts reads them from here.

import type { Inspection } from '@/data/types';

const partsIn = (insp: Inspection, deviceId: string): string[] =>
  (insp.readings ?? []).find(r => r.deviceId === deviceId)?.spareParts ?? [];

/**
 * Parts fitted to one device during one specific visit's inspection.
 * Used when invoicing a visit: that job charges what that job consumed.
 */
export const partsForVisit = (
  deviceId: string,
  inspection?: Inspection
): string[] => (inspection ? partsIn(inspection, deviceId) : []);

/**
 * Parts fitted to one device at its most recent inspection.
 *
 * This is what the device record used to hold, so it keeps the rapport and the
 * whole-register invoice reading the way they did before the parts moved onto
 * the inspection. Inspections are compared by date; ties keep the later entry,
 * which is the later sign-off because `completeVisit` appends.
 */
export const latestPartsFor = (
  deviceId: string,
  inspections: Inspection[]
): string[] => {
  let best: Inspection | undefined;
  inspections.forEach(insp => {
    if (!partsIn(insp, deviceId).length) return;
    if (!best || insp.date >= best.date) best = insp;
  });
  return best ? partsIn(best, deviceId) : [];
};
